import { Component, Inject, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StandortService } from '../../services/standort.service';
import { environment } from '../../../environments/environment';

export interface LabelPreviewData {
  indicator: any;
  standortId: string;
}

@Component({
  selector: 'app-label-preview-dialog',
  templateUrl: './label-preview-dialog.component.html',
  styleUrls: ['./label-preview-dialog.component.scss']
})
export class LabelPreviewDialogComponent implements OnInit {
  htmlContent: string = '';
  loading = true;
  error: string | null = null;
  // UI preview fields
  logoUrl: string = 'https://smarthcaap.tigersoft.at/assets/sbk1-logo.png';
  qrImgUrl: string = '';
  indicatorName: string = '';
  indicatorType: string = 'Indikator';
  indicatorNumber: string = '';
  systemId: string = 'N/A';
  symbolUrl: string | null = null;
  symbolColor: string | null = null;
  today: Date = new Date();

  constructor(
    public dialogRef: MatDialogRef<LabelPreviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LabelPreviewData,
    private standortService: StandortService,
    private snackBar: MatSnackBar,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    // prepare UI preview data
    const ind = this.data.indicator || {};
    this.indicatorName = ind.name || ind.indikator_name || 'Indikator';
    
    // Try multiple paths to get indicator type/code
    // Check nested indikator object first, then direct properties, then type_name
    const indikatorObj = ind.indikator || {};
    this.indicatorType = ind.type_name 
      || indikatorObj.code 
      || ind.indikator_code 
      || indikatorObj.beschreibung 
      || ind.indikator_description 
      || 'Unbekannt';
    
    this.indicatorNumber = this.getFormattedIndicatorNumber(ind.indikator_number);
    this.systemId = ind.system_id ?? 'N/A';
    
    // Get symbol URL and color
    this.symbolUrl = this.getSymbolUrl(ind);
    this.symbolColor = this.getSymbolColor(ind);
    
    // Generate QR using Google Charts - prefer system_id, fall back to UUID
    const qrData = ind.system_id != null && ind.system_id !== undefined 
      ? encodeURIComponent(String(ind.system_id))
      : encodeURIComponent(ind.id || '');
    this.qrImgUrl = `https://chart.googleapis.com/chart?chs=460x460&chld=L|0&cht=qr&chl=${qrData}`;
    // load server HTML (optional) for exact-print view
    this.loadLabelPreview();
  }

  getFormattedIndicatorNumber(indikatorNumber: number | null | undefined): string {
    if (indikatorNumber != null && indikatorNumber !== undefined) {
      return String(indikatorNumber).padStart(3, '0');
    }
    return '000';
  }

  getSymbolUrl(indicator: any): string | null {
    const symbolId = indicator?.indikator?.symbol_id;
    if (!symbolId) return null;
    const key = (environment as any).indikatorSymbolMap?.[symbolId];
    if (!key) return null;
    const base = (environment as any).indikatorSymbolBasePath || '/assets/symbols';
    return `${base}/${key}.svg`;
  }

  getSymbolColor(indicator: any): string | null {
    const symbolId = indicator?.indikator?.symbol_id;
    if (!symbolId) return null;
    const colorMap = (environment as any).indikatorSymbolColorMap || {};
    return colorMap[symbolId] || null;
  }

  loadLabelPreview(): void {
    this.loading = true;
    this.error = null;
    
    console.log('Loading label preview for indicator:', this.data.indicator);
    
    this.standortService.printQRIndicatorLabel(this.data.indicator.id).subscribe({
      next: (htmlContent: string) => {
        this.htmlContent = htmlContent;
        this.loading = false;
        console.log('Label preview loaded successfully');

        // Try to extract server-generated QR, logo, and system_id from HTML for exact UI preview
        try {
          const parser = new DOMParser();
          const doc = parser.parseFromString(htmlContent, 'text/html');
          const qrImg = doc.querySelector('img.qr-code') as HTMLImageElement | null;
          const logoImg = doc.querySelector('img.logo') as HTMLImageElement | null;
          const clientIdEl = doc.querySelector('.client-id');
          if (qrImg && qrImg.src) {
            this.qrImgUrl = qrImg.src;
          }
          if (logoImg && logoImg.src) {
            this.logoUrl = logoImg.src;
          }
          if (clientIdEl && clientIdEl.textContent) {
            // Extract system_id from "ID: {system_id}" text
            const idMatch = clientIdEl.textContent.match(/ID:\s*(.+)/);
            if (idMatch && idMatch[1]) {
              this.systemId = idMatch[1].trim();
            }
          }
        } catch (e) {
          console.warn('Failed to parse HTML for QR/logo extraction', e);
        }
      },
      error: (error) => {
        console.error('Error loading label preview:', error);
        this.error = 'Fehler beim Laden der Label-Vorschau';
        this.loading = false;
        this.snackBar.open('Fehler beim Laden der Label-Vorschau', 'Schließen', { duration: 5000 });
      }
    });
  }

  printLabel(): void {
    if (!this.htmlContent) {
      this.snackBar.open('Keine Label-Daten verfügbar', 'Schließen', { duration: 3000 });
      return;
    }

    // Generate and download PDF
    this.standortService.generateQRIndicatorLabelPDF(this.data.indicator.id).subscribe({
      next: (pdfBlob: Blob) => {
        // Create blob and download
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `qr-label-${this.data.indicator.name}-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        this.snackBar.open('PDF wird heruntergeladen', 'Schließen', { duration: 3000 });
      },
      error: (error) => {
        console.error('Error generating PDF:', error);
        this.snackBar.open('Fehler beim Generieren der PDF', 'Schließen', { duration: 5000 });
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
